import { Clapperboard } from 'lucide-react';
import { ConnectionForm } from './connection-form';
import { ThemeSelector } from '@/components/layout/theme-selector';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useConnection } from '@/hooks/use-connection';
import type { Connection, ProbeOutcome } from '@/lib/connection';

/**
 * What the app shows before it knows where its service is. It says what the page does
 * with the address (talks to it from this browser, sends it nowhere), since that is the
 * only reason pointing a page at your own library is reasonable.
 */
export function SetupScreen(props: { readonly initial?: Connection; readonly outcome?: ProbeOutcome }) {
  const { connect } = useConnection();

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex h-14 items-center px-4">
        <div className="flex items-center gap-2 font-semibold tracking-tight">
          <Clapperboard className="size-5 text-primary" />
          <span>zyx</span>
        </div>
        <div className="ml-auto">
          <ThemeSelector />
        </div>
      </header>

      <main className="flex flex-1 items-start justify-center px-4 py-10 sm:items-center sm:py-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>Connect to your zyx</CardTitle>
            <CardDescription>This app holds no data of its own. Tell it where `zyx serve` is listening and it talks to that address directly from this browser.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <ConnectionForm initial={props.initial} initialOutcome={props.outcome} onConnect={connect} />

            <div className="space-y-2 border-t border-border pt-4 text-xs text-muted-foreground">
              <p>
                <span className="font-medium text-foreground">Reachable over HTTPS?</span> Any address with a certificate works, from any device — including a phone. Your service needs
                <code className="mx-1 rounded bg-muted px-1 py-0.5 font-mono">public.corsOrigins: ["{window.location.origin}"]</code>
                so its browser checks let this page through.
              </p>
              <p>
                <span className="font-medium text-foreground">On this machine?</span> The service listens on <code className="rounded bg-muted px-1 py-0.5 font-mono">http://127.0.0.1:3600</code> by
                default and needs no token there.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
